import { useLayoutEffect, useState, type ReactNode } from "react";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import Button from "../../../components/buttons/Button";
import Image from "../../../components/images/Image";
import Shimmer from "../../AdminManagement/components/ViewAdminDetailsShimmer";
import useApi from "../../../services/useApi";
import useTranslation from "../../../components/CustomHooks/useTranslation";
import { adminContents } from "../../AdminManagement/components/AdminManagementContent";
import config from "../../../config/config";
import { useNavigate, useParams } from "react-router";
import { getFirefoxCssFix } from "../../../components/commonfunctions/getFirefoxCssFix";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import CustomModal from "../../../components/CustomModal";
import { useDispatch } from "react-redux";
import endpoints from "../../../services/endpoints";
import PinVerificationModel from "../../../components/Models/PinVerificationModel";
import TransactionReceipt from "../../Profile/RequestRaised/ETopUpTransactionReciept";
import { fetchAuthSession } from "@aws-amplify/auth";
import { setSelectedTransaction } from "../../../store/slices/transactionSlice";
import { showToast } from "../../../components/toastMessage/toastSlice";
import { convertTimestamp } from "../../../components/commonfunctions/convertDate";
import moment from "moment";
import InputField from "../../../components/inputfields/InputField";
import TextArea from "../../../components/inputfields/TextArea";


/**
 * Props interface for the BaseTopUpRequest component
 * @property {ReactNode} children - Child components to render within the form
 * @property {boolean} showWalletSelection - Whether to show wallet selection options
 * @property {boolean} showPaymentTypeSelection - Whether to show payment type selection options
 * @property {ReactNode} headerContent - Custom header content to display above the form
 * @property {string} defaultPaymentType - Default payment type ("advance" or "paid")
 * @property {string} defaultWallet - Default wallet type
 */
// Updated to match the API response structure
type TransactionDetails = {
  receiver_id: any;
  id: string;
  transaction_id: string;
  transaction_date: string;
  recipient_user_code: string;
  recipient_user_name: string;
  requested_amount: string;
  wallet_type: string;
  payment_status: string;
  status: string;
  source_of_payment: string;
  requester_location: string;
  source_of_request: string;
  transaction_receipt: string;
  note: string;
};

type ETopUpViewProps = {
  defaultPaymentType?: "advance" | "paid";
  defaultWallet?: string;
  headerContent?: ReactNode;
};

export default function ETopUpView({
  defaultPaymentType = "advance",
  defaultWallet = "E Top Up",
}: ETopUpViewProps) {

  // Initial form data with default values from props
  const initialFormData = {
    payment_type: defaultPaymentType,
    choose_wallet: defaultWallet,
    amount: "",
    request_from: {},
    pin_interval: "",
    isDontAskAgainChecked: false,
    receiver_payment_source: "Bank",
    receiver_bank: {},
    sender_payment_source: "Bank",
    sender_bank: {},
    transaction_receipt: "",
    choose_platform: "",
    hesabpay_account_number: "",
    associated_phone_number: "",
  };

  const { getAPI, PatchAPI } = useApi();
  const { notEnglish } = useTranslation(adminContents);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();
  const pathname = useSelector((state: RootState) => state.geolocation.previousUrl);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [receivedAmountModal, setIsReceivedAmountModal] = useState(false)
  const [buttonLoading, setButtonLoading] = useState(false);
  const [modalAction, setModalAction] = useState<"accept" | "reject">("accept");
  const [text, setText] = useState("");
  const [textError, setTextError] = useState("");
  const [noteText, setNoteText] = useState("");
  const [noteTextError, setNoteTextError] = useState("");
  const [isVerificationModelOpen, setIsVerificationModelOpen] = useState(false);
  const [isTransactionReceipt, setIsTransactionReceipt] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  // State for PIN verification
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [pin, setPin] = useState<string[]>(new Array(4).fill(""));
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [responseData, setResponseData] = useState({});
  const [popUpLoading, setPopUpLoading] = useState(false);
  const {
    etopUpreceived,
  } = endpoints;

  const [isPopupPage, setIsOpenPage] = useState(2);
  const dispatch = useDispatch();
  // Initialize state with the new structure
  const [transactionDetails, setTransactionDetails] = useState<TransactionDetails>({
    id: "",
    transaction_id: "",
    transaction_date: "",
    recipient_user_code: "",
    recipient_user_name: "",
    requested_amount: "",
    wallet_type: "",
    payment_status: "",
    status: "",
    source_of_payment: "",
    requester_location: "",
    source_of_request: "",
    transaction_receipt: "",
    note: "",
    bank_charge_mode: "",
    bank_charge_value: "",

  });
  const handleTransactionClick = () => {
    dispatch(setSelectedTransaction({
      receiver_id: transactionDetails?.receiver_id,
      recipient_user_code: transactionDetails?.recipient_user_code,
      recipient_user_name: transactionDetails?.recipient_user_name,
      requested_amount: transactionDetails?.requested_amount,
      confirmed_amount: text,
      note: noteText
    }));
  }

  const fetchTransactionDetails = async () => {
    setLoading(true);
    try {
      // Assuming the endpoint is updated to match the new structure
      const response = await getAPI(`${etopUpreceived}/${id}`);
      if (!response?.error) {
        const temp = response?.data?.data;
        setTransactionDetails(temp);
      } else {
        navigate("/not-found", {
          state: { route: "/e-top-up-request" },
        });
      }
    } catch (error) {
      console.error("Error fetching transaction details:", error);
    }
    setLoading(false);
  };

  useLayoutEffect(() => {
    fetchTransactionDetails();
  }, []);


  // Validation function to check amount format
  const validateAmount = (value: string, requestedAmount?: number): string => {
    // Check if the input is empty
    if (!value.trim()) {
      return "This field is required";
    }

    // Check if the value is greater than requested amount (if provided)
    if (requestedAmount !== undefined) {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue > requestedAmount) {
        return "Amount cannot be greater than requested amount";
      }
      // / Check if value is less than 1
      if (!isNaN(numValue) && numValue < 1) {
        return "Amount must be at least AFA 1.00";
      }
    }

    if (parseFloat(value) < 1) {
      return "Amount must be at least AFA 1.00"
    }
    return "";
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const requestedAmount = parseFloat(
      transactionDetails.requested_amount.replace(/[^0-9.]/g, '')
    );

    const value = e.target.value;

    // Only allow numbers and at most one decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      // Validate decimal places (max 2)
      const parts = value.split('.');
      if (parts.length > 1 && parts[1].length > 2) {
        return; // Prevent input if more than 2 decimal places
      }

      // Validate max length (max 15 characters)
      if (value.length > 15) {
        return; // Prevent input if longer than 15 characters
      }

      // Validate integer part (max 12 digits)
      if (parts[0].length > 12) {
        return; // Prevent input if more than 12 digits before decimal
      }

      setText(value);
      if (value !== '' && !value.startsWith('0.')) {
        // Remove leading zeros but preserve "0." for decimal values less than 1
        const normalizedValue = value.replace(/^0+(\d)/, '$1');
        setText(normalizedValue);
        setTextError(validateAmount(normalizedValue, requestedAmount));
      } else {
        setText(value);
        setTextError(validateAmount(value, requestedAmount));
      }
      setTextError(validateAmount(value, requestedAmount)); // Pass the requested amount
    }
  };


  const handleTransactionStatus = async () => {
    if (transactionDetails?.payment_status !== 'Advance') {

      const trimmedNote = noteText.trim();
      let validationErrors = false;
      if (trimmedNote.length > 250) {
        setNoteTextError("Note cannot exceed 250 characters");
        validationErrors = true;
      } else if (!trimmedNote) {
        setNoteTextError("This field is required");
        validationErrors = true;
      } else {
        setNoteTextError("");
      }
      const requestedAmountValue = transactionDetails.requested_amount.toString().replace(/[^0-9.]/g, '');

      if (validationErrors) return;
      if (modalAction === "accept") {
        const amountValidation = validateAmount(text, Number(requestedAmountValue));
        if (amountValidation) {
          setTextError(amountValidation);
          validationErrors = true;
          return
        } else {
          setTextError("");
        }
      } else {
        setButtonLoading(true);
        const response = await PatchAPI(`cash-in/status/${id}`, { status: 'Rejected', rejection_reason: trimmedNote });
        if (!response.error) {
          dispatch(showToast({
            type: "success",
            message: "Request rejected successfully"
          }));
        } else {
          dispatch(showToast({
            type: "error",
            message: response?.data?.response?.data?.message || 'Something went wrong'
          }));
          setIsModalOpen(false);
        }
        setButtonLoading(false);
        navigate(`/e-top-up-request/e-top-up`)
      }

    } else {
      if (modalAction === 'accept') {
        handleSubmitPopup()
        return
      } else {
        const trimmedNote = noteText.trim();
        let validationErrors = false;
        if (!trimmedNote) {
          setNoteTextError("This field is required");
          validationErrors = true;
        } else {
          setNoteTextError("");
        }

        if (validationErrors) return;
        setButtonLoading(true);
        const response = await PatchAPI(`cash-in/status/${id}`, { status: 'Rejected', rejection_reason: trimmedNote });
        console.log(response, "ljsadnj")
        if (!response.error) {
          dispatch(showToast({
            type: "success",
            message: "Request rejected successfully"
          }));
        } else {
          dispatch(showToast({
            type: "error",
            message: response?.data?.response?.data?.message || 'Something went wrong'
          }));
        }
        setButtonLoading(false);
        setIsModalOpen(false);
      navigate(`/e-top-up-request/e-top-up`)
      }
      
    }

    // Proceed to next steps
    setButtonLoading(true);
    setIsModalOpen(false);
    if (transactionDetails?.payment_status !== 'Advance' && modalAction !== "reject") {
      setIsReceivedAmountModal(true);
    }
    setButtonLoading(false);
  };


  /**
   * Handles input changes for form fields with validation
   * @param {string} key - The field name to update
   * @param {string|boolean|any[]} value - The new value for the field
   */
  const handleInputChange = (key: string, value: string | boolean | any[]) => {
    // Validate amount field to ensure it's a valid number with up to 2 decimal places
    if (key === "amount" && typeof value === "string") {
      const regex = /^\d*\.?\d{0,2}$/;
      if (!regex.test(value)) return;
    }

    // Validate phone number format for associated_phone_number field
    if (
      key === "associated_phone_number" &&
      typeof value === "string" &&
      value.length > 0
    ) {
      // This regex ensures:
      // 1. "+" symbol only allowed at the beginning
      // 2. No consecutive spaces or hyphens
      // 3. Only numbers, spaces, and hyphens allowed after optional leading "+"
      const regex = /^\+?(?!.*[ -][ -])(?!.*[+].*[+])[0-9 -]*$/;
      if (!regex.test(value)) return;
    }

    // Remove spaces from hesabpay account number
    if (key === "hesabpay_account_number") {
      value = value.replace(/\s/g, ""); // Remove spaces
    }

    // Reset form when payment type changes
    if (key === "payment_type") {
      setText("");
      setFormData(initialFormData);
      setErrors({});
    }

    // Handle special case: when receiver payment source is Cash, set sender payment source to Cash too
    if (key === "receiver_payment_source" && value === "Cash") {
      setFormData((prevData) => ({
        ...prevData,
        ["sender_payment_source"]: value,
      }));
    }

    // Reset bank fields when receiver payment source changes
    if (key === "receiver_payment_source") {
      setFormData((prevData) => ({
        ...prevData,
        ["receiver_bank"]: {},
        ["sender_bank"]: {},
        ["transaction_receipt"]: "",
      }));
    }

    // Reset sender bank when sender payment source changes
    if (key === "sender_payment_source") {
      setFormData((prevData) => ({
        ...prevData,
        ["sender_bank"]: {},
      }));
    }

    // When receiver payment source is Bank, set sender payment source to Bank too
    if (key === "receiver_payment_source" && value === "Bank") {
      setFormData((prevData) => ({
        ...prevData,
        ["sender_payment_source"]: value,
      }));
    }

    // Update form data with new value
    setFormData((prevData) => ({
      ...prevData,
      [key]: value,
    }));

    // Clear error for the updated field
    setErrors((prevErrors) => ({
      ...prevErrors,
      [key]: "",
    }));

    // Reset related fields when platform changes
    if (key === "choose_platform") {
      setFormData((prevData) => ({
        ...prevData,
        hesabpay_account_number: "",
        associated_phone_number: "",
        transaction_receipt: "",
      }));
      setErrors((prevErrors) => ({
        ...prevErrors,
        hesabpay_account_number: "",
        associated_phone_number: "",
        transaction_receipt: "",
      }));
    }

    // Reset all payment-related fields when receiver payment source changes
    if (key === "receiver_payment_source") {
      setFormData((prevData) => ({
        ...prevData,
        receiver_bank: {},
        sender_bank: {},
        transaction_receipt: "",
        choose_platform: "",
        hesabpay_account_number: "",
        associated_phone_number: "",
      }));
      setErrors((prevErrors) => ({
        ...prevErrors,
        receiver_bank: "",
        sender_bank: "",
        transaction_receipt: "",
        choose_platform: "",
        hesabpay_account_number: "",
        associated_phone_number: "",
      }));
    }
  };

  const handleClickVerify = async (isPin: boolean) => {
    setVerifyLoading(true);
    try {
      // Validate PIN if required
      if (isPin) {
        // A prior "Invalid PIN" message is intentionally left on screen here (rather than
        // cleared unconditionally) so it stays visible alongside the interval error below -
        // the PIN itself hasn't changed since that last failed verification.
        let hasValidationError = false;
        if (pin.includes("")) {
          setErrorMessage("This field is required");
          hasValidationError = true;
        }
        if (formData.isDontAskAgainChecked && formData.pin_interval === "") {
          setErrors((prevErrors) => ({
            ...prevErrors,
            pin_interval: "This field is required",
          }));
          hasValidationError = true;
        }
        if (hasValidationError) {
          return;
        }
      }

      // Determine which API endpoint to use
      let endpoint = '';
      let payload = {};
      if (transactionDetails?.payment_status !== 'Advance') {
        endpoint = `cash-in/approve/${id}`;

        // Prepare payload for API request
        // const transaction_id = uuidv4()
        payload = {
          confirmed_amount: Number(text),
          e_topup_stock_amount: Number(text),
          due_transaction_id: null,
          due_amount: null,
          note: noteText,
          pin: pin.join(""),
          pin_interval: formData?.pin_interval,
        };
      } else {
        endpoint = `cash-in/status/${id}`;

        // Prepare payload for API request
        // const transaction_id = uuidv4()
        payload = {
          "status": "Approved",
          "pin": pin.join(''),
          ...(formData?.pin_interval && { "pin_interval": formData?.pin_interval }),
        }
      }

      // Send API request
      const response = await PatchAPI(endpoint, payload);
      if (response.error) {
        // Handle API error
        setErrorMessage(response?.data?.response?.data?.message);
      } else {
        setIsReceivedAmountModal(false);
        // Handle successful response
        setResponseData(response?.data?.data);
        setIsTransactionReceipt(true);
        setIsVerificationModelOpen(false);
        setText("");
      }
    } catch (error) {
      // Handle unexpected errorsPostAPI
      console.error(error);
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      // Reset loading states
      setVerifyLoading(false);
      setPopUpLoading(false);
      setIsModalOpen(false);
    }
  };

  const openActionModal = (action: "accept" | "reject") => {
    setModalAction(action);
    const requestedAmountValue = transactionDetails.requested_amount.toString().replace(/[^0-9.]/g, '');
    setText(requestedAmountValue);
    setIsModalOpen(true);
  };

  // Format date string to more readable format
  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Check if transaction status is Pending
  const isPendingTransaction = transactionDetails?.status === "Pending";

  const handleClickDone = () => {
    navigate(`/e-top-up-request/e-top-up`)
    setResponseData({});
    setText('');
    setNoteText('');
    setFormData(initialFormData);
    setIsTransactionReceipt(false);
    setIsReceivedAmountModal(false)
    setIsModalOpen(false)
  };

  /**
    * Handles submission from the confirmation popup
    * Manages multi-step confirmation process and PIN verification
    */
  const handleSubmitPopup = async () => {
    setPopUpLoading(true);
    setButtonLoading(true);

    // Handle first page of confirmation popup
    if (isPopupPage === 1 && transactionDetails?.payment_status === "Paid") {
      setIsOpenPage(2);
      setPopUpLoading(false);
      setButtonLoading(false);
      return;
    } else {
      // Check if PIN verification is needed based on expiry timestamp
      const user = await fetchAuthSession({ forceRefresh: true });
      const timestamp =
        user?.tokens?.idToken?.payload?.["custom:pin_expiry_timestamp"];
      console.log("near fetch auth session", user?.tokens?.idToken)
      if (timestamp) {
        const inputDate = new Date(timestamp);
        const currentDate = new Date();
        // setIsModalOpen(false);
        if (inputDate < currentDate) {
          // PIN expired, show verification modal
          setIsVerificationModelOpen(true);
        } else {
          // PIN still valid, proceed without verification
          handleClickVerify(false);
        }
      } else {
        // No timestamp, show verification modal
        setPopUpLoading(false);
        setIsVerificationModelOpen(true);
      }
      setButtonLoading(false);
      setIsReceivedAmountModal(false);
      setIsModalOpen(false);
      setNoteText('');
    }
  };
  const getProfileBgColor = (status: any) => {
    if (status === "Approved") {
      return "bg-[#E8F7ED]";
    } else if (status === "In Review") {
      return "bg-[#E5F0FC]";
    } else if (status === "Pending" || status === "In Progress") {
      return "bg-[#FFEFD9]";
    } else if (status === "Rejected") {
      return "bg-[#FFE5E5]";
    } else {
      return "";
    }
  }

  const getProfileTextColor = (status: any) => {
    if (status === "Approved") {
      return "text-[#22B14C]";
    } else if (status === "In Review") {
      return "text-[#0072E4]";
    } else if (status === "Pending" || status === "In Progress") {
      return "text-[#FD9101]";
    } else if (status === "Rejected") {
      return "text-[#FF0000]";
    } else {
      return "";
    }
  }
  return (
    <div dir={notEnglish ? "rtl" : "ltr"}>
      <Breadcrumbs
        navPath={["E Top Up Request", "List of E Top Up Request Raised", "View Transaction Details"]}
        links={pathname ? [pathname, pathname] : [
          "/raise-e-top-up-request",
          "/e-top-up-request",
        ]}
        heading="View Transaction Details"
      />

      {!loading ? (
        <div className="mt-1.5 bg-white h-maintableheight rounded-lg flex flex-col">
          <div className="px-4 sm:px-6 py-5 border-b border-[#E5E9EB] flex flex-wrap justify-between items-start gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-heading2">
                  Transaction ID - <span data-testid="Transaction-id">{transactionDetails?.transaction_id || "-"}</span>
                </h1>
                {transactionDetails?.status != "Pending" && (<div
                  className={`inline-flex items-center px-3 py-1 rounded-lg shrink-0 ${getProfileBgColor(transactionDetails?.status)}`}
                >
                  <h1 data-testid="user-status" className={`text-sm font-medium ${getProfileTextColor(transactionDetails?.status)}`}>
                    {transactionDetails?.status}
                  </h1>
                </div>)}
              </div>
              <p className="text-subTitle" data-testid="transaction-date">
                {transactionDetails?.transaction_date ? moment(transactionDetails?.transaction_date).format(
                  "DD-MM-YYYY, hh:mm A"
                ) : '-'}
              </p>
            </div>
            {isPendingTransaction && (
              <div className="flex flex-wrap gap-3">
                <Button
                  buttonName="Accept"
                  className="bg-positive py-2 h-fit px-6 cursor-pointer"
                  bgColor="bg-positive"
                  textSize="text-large"
                  testId="accept-btn"
                  buttonOnclick={() => openActionModal("accept")}
                />
                <Button
                  buttonName="Reject"
                  className="bg-negative py-2 h-fit px-6 cursor-pointer"
                  bgColor="bg-negative"
                  textSize="text-large"
                  testId="reject-btn"
                  buttonOnclick={() => openActionModal("reject")}
                />
              </div>
            )}
          </div>
          <div
            className={`grow px-6 pb-4 my-4 h-viewAdminHeight ${getFirefoxCssFix("scroll")} overflow-y-scroll`}

          >
            {/* Transaction Details Section */}
            <div className="mt-6">
              <h1 className="text-heading3 underline underline-offset-4">
                Transaction Details
              </h1>
              <div className="mt-3.5">
                <div className="-mx-5 flex flex-wrap">
                  {/* Recipient User Code */}
                  <div className="px-5 w-full sm:w-1/2 md:w-1/3 mb-6">
                    <h1 className="text-body2 text-primary_text">
                      Recipient User Code
                    </h1>
                    <p
                      data-testid="recipient-user-code"
                      dir="ltr"
                      className="text-body1 text-primary_text max-w-full text-ellipsis overflow-hidden"
                    >
                      {transactionDetails.recipient_user_code || "-"}
                    </p>
                  </div>

                  {/* Recipient User Name */}
                  <div className="px-5 w-full sm:w-1/2 md:w-1/3 mb-6">
                    <h1 className="text-body2 text-primary_text">
                      Recipient User Name
                    </h1>
                    <p
                      data-testid="recipient-user-name"
                      dir="ltr"
                      className="text-body1 text-primary_text wrap-break-word whitespace-normal"
                    >
                      {transactionDetails.recipient_user_name || "-"}
                    </p>
                  </div>

                  {/* Requested Amount */}
                  <div className="px-5 w-full sm:w-1/2 md:w-1/3 mb-6">
                    <h1 className="text-body2 text-primary_text">
                      Requested Amount
                    </h1>
                    <p
                      data-testid="requested-amount"
                      dir="ltr"
                      className="text-body1 text-primary_text max-w-full text-ellipsis overflow-hidden"
                    >
                      AFA {transactionDetails.requested_amount || "-"}
                    </p>
                  </div>

                  {/* Wallet Type */}
                  <div className="px-5 w-full sm:w-1/2 md:w-1/3 mb-6">
                    <h1 className="text-body2 text-primary_text">
                      Wallet Type
                    </h1>
                    <p
                      data-testid="wallet-type"
                      dir="ltr"
                      className="text-body1 text-primary_text max-w-full text-ellipsis overflow-hidden"
                    >
                      {transactionDetails.wallet_type || "-"}
                    </p>
                  </div>

                  {/* Payment Status */}
                  <div className="px-5 w-full sm:w-1/2 md:w-1/3 mb-6">
                    <h1 className="text-body2 text-primary_text">
                      Payment Status
                    </h1>
                    <p
                      data-testid="payment-status"
                      dir="ltr"
                      className="text-body1 text-primary_text max-w-full text-ellipsis overflow-hidden"
                    >
                      {transactionDetails.payment_status || "-"}
                    </p>
                  </div>

                  {/* Transaction Status */}
                  {transactionDetails.payment_status == "Paid" && <div className="px-5 w-full sm:w-1/2 md:w-1/3 mb-6">
                    <h1 className="text-body2 text-primary_text">
                      Source of Payment
                    </h1>
                    <p
                      data-testid="transaction-status"
                      dir="ltr"
                      className="text-body1 text-primary_text max-w-full text-ellipsis overflow-hidden"
                    >
                      {transactionDetails.source_of_payment || "-"}
                    </p>
                  </div>}
                </div>
                {/* Requester Location */}
                <div className="-mx-5 flex flex-wrap">
                  <div className="px-5 w-full sm:w-1/2 md:w-1/3 mb-6">
                    <h1 className="text-body2 text-primary_text">
                      Requester Location
                    </h1>
                    <p
                      data-testid="requester-location"
                      dir="ltr"
                      className="text-body1 text-primary_text max-w-full text-ellipsis overflow-hidden"
                    >
                      {transactionDetails.requester_location || "-"}
                    </p>
                  </div>

                  {/* Source of Request */}
                  <div className="px-5 w-full sm:w-1/2 md:w-1/3 mb-6">
                    <h1 className="text-body2 text-primary_text">
                      Source of Request
                    </h1>
                    <p
                      data-testid="source-of-request"
                      dir="ltr"
                      className="text-body1 text-primary_text max-w-full text-ellipsis overflow-hidden"
                    >
                      {transactionDetails.source_of_request || "-"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bank Transfer Receipt */}
            {transactionDetails?.transaction_receipt && (<div className="mt-6">
              <p className="text-heading3 underline underline-offset-4">
                Transfer Receipt
              </p>
              {transactionDetails?.transaction_receipt ? (
                <div className="w-[340px] flex justify-between overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1">
                  <p>{transactionDetails.transaction_receipt.split('/').pop() || "Receipt"}</p>
                  <Image
                    src="file-eye"
                    className="cursor-pointer"
                    objectFit="contain"
                    onClick={() => {
                      const fileUrl = config?.cdnURL + 'public/' + transactionDetails.transaction_receipt;
                      window.open(fileUrl, "_blank");
                    }}
                    data-testid="transfer-receipt"
                  />
                </div>
              ) : (
                <p className="mt-1 font-medium text-gray-900">-</p>
              )}
            </div>)}

            {/* Note */}
            <div className="mt-6">
              <p className="text-heading3 underline underline-offset-4">
                Note
              </p>
              <p className="mt-2 text-body1 text-justify wrap-break-word" data-testid="note">
                {transactionDetails?.note || "-"}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <Shimmer />
      )}

      {isModalOpen && (
        <CustomModal
          isOpen={isModalOpen}
          title={modalAction === "accept" ? (transactionDetails?.payment_status === 'Advance' ? 'Accept Request Approval' : 'Accept Transaction') : "Confirm Rejection?"}
          subText={modalAction === "accept"
            ? `Are you sure you want to ${transactionDetails?.payment_status === 'Advance' ? 'approve the following E Top Up advance request' : 'accept the transaction'}?`
            : "Are you sure you want to reject this E Top Up stock purchase request?"}
          primaryButtonText={modalAction === "accept" ? "Accept" : "Reject"}
          secondaryButtonText="Cancel"
          handelSubmit={() => {
            handleTransactionStatus()
          }}
          buttonColor={modalAction === "accept" ? "bg-positive" : "bg-negative"}
          textArea={false}
          buttonLoading={buttonLoading}
          onHandleClose={() => {
            if (modalAction === "reject") {
              setNoteText('')
            }
            setIsModalOpen(false)
          }}
          // note={modalAction === "accept" ? "Note: There is 2% bank charges associated" : undefined}
          onClose={() => {

          }}
          secondaryButton={transactionDetails?.payment_status === 'Advance'}
          buttonWidth={modalAction === 'reject' ? 'w-[77px]' : 'w-[100px]'}
        >
          {/* Add this close button if your CustomModal doesn't have built-in support */}
          <div className="absolute top-4 right-4">
            <button
              onClick={() => {
                setIsModalOpen(false)
                setTextError('')
                setNoteTextError('')
                setNoteText('')
              }}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <img src="/images/close.svg" />
            </button>
          </div>

          {modalAction === "accept" && (
            (transactionDetails?.payment_status !== 'Advance') ?
              (<div className="my-4" data-testid="accept_model_details">
                <div className="flex justify-between mb-4">
                  <div>
                    <h3 className="text-body2 text-primary_text">Recipient User Name</h3>
                    <p className="text-body1 text-primary_text">{transactionDetails.recipient_user_name}</p>
                  </div>
                  <div>
                    <h3 className="text-body2 text-primary_text">Recipient User Code</h3>
                    <p className="text-body1 text-primary_text">{transactionDetails.recipient_user_code}</p>
                  </div>
                </div>

                <div className="mb-4">
                  <h3 className="text-body2 text-primary_text">Requested Amount</h3>
                  <p className="text-body1 text-primary_text">AFA {transactionDetails.requested_amount}</p>
                </div>

                <div className="mb-2">
                  <InputField
                    inputItem={{
                      labelName: "Received Amount",
                      id: "received-amount",
                    }}
                    placeholder={"Enter amount"}
                    errorMessage={textError}
                    value={text}
                    required={true}
                    disabled={false}
                    tabIndex={0}
                    testId={'received-amount'}
                    maxLength={15}
                    onChange={(e) => {
                      handleAmountChange(e);
                    }}
                    maindivstyle={`w-full ${textError ? '' : 'mb-[34px]'}`}
                    className={'w-full'}
                  />
                </div>

                <div className="mb-2">
                  <TextArea
                    top="pt-0"
                    label="Note"
                    text={noteText}
                    setText={setNoteText}
                    textError={noteTextError}
                    setTextError={setNoteTextError}
                    height={"h-[180px]"}
                  />
                </div>
                {(transactionDetails.bank_charge_mode == "PERCENTAGE" && Number(transactionDetails?.bank_charge_value) > 0 && transactionDetails?.bank_charge_value != null) ? <div className="text-sm text-secondary_text mt-1 text-justify">
                  Note: There is {transactionDetails?.bank_charge_value} bank charges associated. Ledger entry will be created once the transaction is completed
                </div>
                  :
                  <div className="text-sm text-secondary_text mt-1">
                  </div>}
              </div>) : (
                <div className="">
                  <h2 className="subTitle text-secondary_text">{`User Code: ${transactionDetails?.recipient_user_code || '-'}`}</h2>
                  <h2 className="subTitle text-secondary_text">{`User Name: ${transactionDetails?.recipient_user_name || '-'}`}</h2>
                  <h2 className="subTitle text-secondary_text">{`Requested Amount: AFA ${transactionDetails?.requested_amount || '-'}`}</h2>
                </div>
              )
          )}

          {modalAction === "reject" && (
            <div className="my-4">
              <div className="mb-4">
                <div className="mb-2">
                  <TextArea
                    top="pt-0"
                    label="Reason"
                    text={noteText}
                    setText={setNoteText}
                    textError={noteTextError}
                    setTextError={setNoteTextError}
                    height={"h-[180px]"}
                  />
                </div>
              </div>
            </div>
          )}
        </CustomModal>
      )}

      {receivedAmountModal && (<CustomModal
        isOpen={receivedAmountModal}
        title={"Clear Due"}
        subText={"Do you want to clear the due?"}
        primaryButtonText={"Yes"}
        secondaryButtonText="No"
        handelSubmit={() => {
          setIsReceivedAmountModal(false)
          navigate(`/e-top-up-request/transactions/${id}`)
          handleTransactionClick()
        }}
        buttonColor={modalAction === "accept" ? "bg-p_primary" : "bg-negative"}
        textArea={false}
        secondaryButtonLoading={popUpLoading}
        note={"If not, the confirmed amount will be converted into E Top-Up stocks"}
        secondaryButton={true}
        onHandleClose={() => {
          handleSubmitPopup()
        }}
        onClose={() => {
          ''
        }}
      >
        <div className="absolute top-4 right-4">
          <button
            onClick={() => {
              setIsReceivedAmountModal(false)
              setNoteText('')
            }}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <img src="/images/close.svg" />
          </button>
        </div>
      </CustomModal>
      )}
      {/* PIN verification modal */}
      {isVerificationModelOpen && (
        <PinVerificationModel
          handleClose={() => {
            setIsVerificationModelOpen(false)
            setLoading(true);
            setPin(new Array(4).fill(""));
            setErrorMessage("");
            setPopUpLoading(false);
            setFormData((prevData) => ({
              ...prevData,
              ["isDontAskAgainChecked"]: false,
            }));

            // Show confirmation popup
            setLoading(false);
            setIsOpenPage(1);
            if (transactionDetails?.payment_status === 'Advance') {
              setIsReceivedAmountModal(false)
            }
          }}
          pin={pin}
          setPin={setPin}
          handleDropdownChange={handleInputChange}
          selectedDropDownValue={formData?.pin_interval}
          isDontAskAgainChecked={formData?.isDontAskAgainChecked}
          handleClickVerify={() => handleClickVerify(true)}
          verifyLoading={verifyLoading}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          dropdownError={errors?.pin_interval}
        />
      )}

      {/* Transaction receipt view */}
      {isTransactionReceipt && (
        <TransactionReceipt
          details={responseData}
          handleClickDone={handleClickDone}
          defaultPaymentType={defaultPaymentType}
          advance={transactionDetails?.payment_status === 'Advance'}
        />
      )}
    </div>
  );
}